import * as React from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { useFormik } from 'formik'
import { Col } from 'antd'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import { map } from 'lodash'

import { ALL_INDUSTRIES } from '../../../../../queries/AllIndustries'
import { UPDATE_BRAND_PROFILE } from '../../../../../mutations/UpdateBrandProfile'
import { client as apolloClient } from '../../../../ApolloProvider/ApolloProvider'
import {
  Brand as BrandType,
  BrandCoolness,
  BrandHappiness,
  BrandHistory,
  BrandSize,
} from '../../../../../typings/types'

import DefaultButton from '../../../components/DefaultButton/DefaultButton'
import MultiSelect from '../../MultiSelect'
import {
  BrandCoolnessLabels,
  BrandHappinessLabels,
  BrandHistoryLabels,
  BrandSizeLabels,
  functionOption,
} from '../../config'
import {
  PageTitle,
  WhiteBox,
  InputLabel,
  ButtonWrapper,
  StyledRow,
  StyledSelect,
} from '../../BrandDashboardSettings.style'

interface BrandOtherDataFormProps {
  brandProfile?: BrandType
  brandOrderUuid?: string
}

const BrandOtherDataForm: React.FC<BrandOtherDataFormProps> = ({
  brandProfile,
  brandOrderUuid,
}) => {
  const [otherDataInitialState, useOtherDataInitialState] = React.useState<any>({
    coolness: null,
    happiness: null,
    history: null,
    size: null,
  })
  const [functionsState, setFunctionsState] = React.useState<any>([])
  const [industriesState, setIndustriesState] = React.useState<any>([])

  const formikRef = React.useRef<any>()

  const [updateBrandProfile, { loading: brandDetailsUpdating }] = useMutation(
    UPDATE_BRAND_PROFILE,
    { client: apolloClient }
  )
  const { data: allIndustries } = useQuery(ALL_INDUSTRIES, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    client: apolloClient,
  })
  const refactoredIndustries =
    allIndustries &&
    allIndustries.allIndustries &&
    allIndustries.allIndustries.map(({ uuid, name }) => ({ label: name, value: uuid }))
  const functionsDefaultOptions: any = functionOption.filter(obj => {
    return functionsState && functionsState.some(v => obj.value === v)
  })
  const industriewsDefaultOptions: any =
    refactoredIndustries &&
    refactoredIndustries.filter(obj => {
      return industriesState && industriesState.some(v => obj.value === v)
    })

  React.useEffect(
    () => {
      useOtherDataInitialState({
        coolness: (brandProfile && brandProfile.coolness) || null,
        happiness: (brandProfile && brandProfile.happiness) || null,
        history: (brandProfile && brandProfile.history) || null,
        size: (brandProfile && brandProfile.size) || null,
      })
      brandProfile && setFunctionsState(brandProfile.functions)
      brandProfile &&
        brandProfile.industries &&
        setIndustriesState(brandProfile.industries.map(item => item.uuid))
    },
    [brandProfile]
  )

  const otherDataformik = useFormik({
    initialValues: {
      ...otherDataInitialState,
      functions: functionsState,
      industries: industriesState,
    },
    enableReinitialize: true,
    onSubmit: values => {
      updateBrandProfile({
        variables: {
          brandUuid: brandOrderUuid,
          brandUpdateInput: {
            functions: values.functions,
            coolness: values.coolness,
            happiness: values.happiness,
            history: values.history,
            size: values.size,
            industryUuids: industriesState,
          },
        },
      })
    },
  })

  const { Option } = StyledSelect
  return (
    <>
      <PageTitle>Other data</PageTitle>
      <form onSubmit={otherDataformik.handleSubmit} ref={formikRef}>
        <WhiteBox>
          <StyledRow type="flex">
            <Col xs={24} sm={24} md={24} lg={24}>
              <InputLabel>Functions</InputLabel>
              <MultiSelect
                className={otherDataformik.values.functions === '' ? '' : 'filled'}
                options={functionOption}
                selectValue={functionsDefaultOptions}
                name="functions"
                placeholder={'Pick a new function'}
                onChange={value => setFunctionsState(value.map(item => item.value))}
              />
            </Col>
          </StyledRow>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Coolness</InputLabel>
              <StyledSelect
                className={otherDataformik.values.coolness === '' ? '' : 'filled'}
                name="coolness"
                value={otherDataformik.values.coolness}
                onChange={value => {
                  otherDataformik.setFieldValue('coolness', value)
                  useOtherDataInitialState({ ...otherDataInitialState, coolness: value })
                }}
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
              >
                <Option value="" disabled>
                  Pick coolness
                </Option>
                {map(BrandCoolness, (obj, index) => (
                  <Option key={`${index}-${obj}`} value={BrandCoolness[obj]}>
                    {BrandCoolnessLabels[obj]}
                  </Option>
                ))}
              </StyledSelect>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Happiness</InputLabel>
              <StyledSelect
                className={otherDataformik.values.happiness === '' ? '' : 'filled'}
                name="happiness"
                value={otherDataformik.values.happiness}
                onChange={value => {
                  otherDataformik.setFieldValue('happiness', value)
                  useOtherDataInitialState({ ...otherDataInitialState, happiness: value })
                }}
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
              >
                <Option value="" disabled>
                  Pick a department
                </Option>
                {map(BrandHappiness, (obj, index) => (
                  <Option key={`${index}-${obj}`} value={BrandHappiness[obj]}>
                    {BrandHappinessLabels[obj]}
                  </Option>
                ))}
              </StyledSelect>
            </Col>
          </StyledRow>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Video history</InputLabel>
              <StyledSelect
                className={otherDataformik.values.history === '' ? '' : 'filled'}
                name="history"
                value={otherDataformik.values.history}
                onChange={value => {
                  otherDataformik.setFieldValue('history', value)
                  useOtherDataInitialState({ ...otherDataInitialState, history: value })
                }}
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
              >
                <Option value="" disabled>
                  Pick a department
                </Option>
                {map(BrandHistory, (obj, index) => (
                  <Option key={`${index}-${obj}`} value={BrandHistory[obj]}>
                    {BrandHistoryLabels[obj]}
                  </Option>
                ))}
              </StyledSelect>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Size</InputLabel>
              <StyledSelect
                className={otherDataformik.values.size === '' ? '' : 'filled'}
                name="size"
                value={otherDataformik.values.size}
                onChange={value => {
                  otherDataformik.setFieldValue('size', value)
                  useOtherDataInitialState({ ...otherDataInitialState, size: value })
                }}
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
              >
                <Option value="" disabled>
                  Pick a department
                </Option>
                {map(BrandSize, (obj, index) => (
                  <Option key={`${index}-${obj}`} value={BrandSize[obj]}>
                    {BrandSizeLabels[obj]}
                  </Option>
                ))}
              </StyledSelect>
            </Col>
          </StyledRow>
          <StyledRow type="flex">
            <Col xs={24} sm={24} md={24} lg={24}>
              <InputLabel>Industries</InputLabel>
              <MultiSelect
                className={otherDataformik.values.industries === '' ? '' : 'filled'}
                options={refactoredIndustries}
                selectValue={industriewsDefaultOptions}
                name="industries"
                placeholder={'Pick a new industry'}
                onChange={value => {
                  setIndustriesState(value.map(item => item.value))
                  otherDataformik.setFieldValue('industries', value.map(item => item.value))
                }}
              />
            </Col>
          </StyledRow>
          <ButtonWrapper>
            <DefaultButton
              fontSize="1rem"
              padding="0 1.625rem"
              backgroundColor={colors.adaptBlue}
              style={{ marginLeft: 'auto' }}
            >
              {brandDetailsUpdating ? 'Loading...' : 'Save changes'}
            </DefaultButton>
          </ButtonWrapper>
        </WhiteBox>
      </form>
    </>
  )
}

export default BrandOtherDataForm



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardSettings/forms/BrandOtherDataForm/BrandOtherDataForm.tsx